// The shell's decisions about modules, as pure functions so node --test can reach them
// (this repository has no DOM harness; web/src/session/roles.js is the precedent).
//
// THESE DECIDE WHAT IS SHOWN AND WHERE TO GO, NEVER WHAT IS ALLOWED. The server's
// requireProduct gate per /api/<module> and requireProductRole inside routers enforce.

import { atLeastRole } from './session/roles.js';

/** Mirrors src/auth/role-map.js PRODUCTS exactly; held to it by a test. */
export const PRODUCTS = ['support', 'sales', 'hardware', 'implementation'];

/**
 * The old Support page prefixes. The server answers each with a PERMANENT 301 to
 * /support (src/routes/support-redirects.js OLD_PREFIXES, held equal by a test), and
 * browsers cache a 301 indefinitely, so a module mounted at one of these would be
 * hijacked into Support in every browser that ever followed an old link. Reserved forever.
 */
export const OLD_SUPPORT_PREFIXES = Object.freeze(['tickets', 'customers', 'reports', 'admin', 'search']);

/**
 * Throws on any manifest the shell could not route or gate correctly; returns the list
 * frozen. Called once, at import of web/shell/registry.js, so a bad manifest fails at
 * boot in the console rather than as a wrong page later.
 */
export function validateManifests(manifests) {
  const ids = new Set();
  const orders = new Set();
  let roots = 0;
  for (const mf of manifests) {
    const where = `module manifest '${mf?.id}'`;
    if (ids.has(mf.id)) throw new Error(`${where}: duplicate id`);
    ids.add(mf.id);
    if (orders.has(mf.order)) throw new Error(`${where}: duplicate order ${mf.order}`);
    orders.add(mf.order);
    if (!PRODUCTS.includes(mf.product)) throw new Error(`${where}: unknown product '${mf.product}'`);
    if (typeof mf.load !== 'function') throw new Error(`${where}: load must be a function`);
    if (mf.basePath === '/') {
      roots += 1;
      if (roots > 1) throw new Error(`${where}: a second root ('/') module`);
    } else {
      // `/<id>` exactly: the URL prefix IS the module id (spec decision 5), and it is the
      // value users.last_module stores.
      if (mf.basePath !== `/${mf.id}`) {
        throw new Error(`${where}: basePath must be '/${mf.id}' (or '/' for a root module)`);
      }
      if (OLD_SUPPORT_PREFIXES.includes(mf.id)) {
        throw new Error(`${where}: basePath '${mf.basePath}' is reserved: browsers cache its permanent redirect to /support`);
      }
    }
    const inside = mf.basePath === '/' || mf.home === mf.basePath || mf.home.startsWith(`${mf.basePath}/`);
    if (!inside) throw new Error(`${where}: home '${mf.home}' is outside basePath '${mf.basePath}'`);
  }
  return Object.freeze([...manifests]);
}

const holds = (user, product) => (user?.products ?? []).some((p) => p.product === product);
const enabled = (user, id) => (user?.enabled_modules ?? []).includes(id);

/** Registered ∩ enabled here ∩ a role held in its product, in `order`. */
export function availableModules(manifests, user) {
  return manifests
    .filter((mf) => enabled(user, mf.id) && holds(user, mf.product))
    .sort((a, b) => a.order - b.order);
}

/** The spec's landing rule. `available` must already be sorted by order. */
export function pickLanding(available, lastModule) {
  if (available.length === 0) return null;
  if (available.length === 1) return available[0];
  return available.find((mf) => mf.id === lastModule) ?? available[0];
}

/** Which module owns a path: a `/<id>` prefix at a segment boundary, else a root module. */
export function moduleForPath(pathname, manifests) {
  const prefixed = manifests.find((mf) => mf.basePath !== '/'
    && (pathname === mf.basePath || pathname.startsWith(`${mf.basePath}/`)));
  return prefixed ?? manifests.find((mf) => mf.basePath === '/') ?? null;
}

/**
 * A module's nav items this user may see, by rank in that module's product. A missing
 * `requires` counts as `null` (open): a JS module may leave the key out, and reading
 * `.role` off undefined would throw in the header.
 */
export function visibleNav(nav, user, product) {
  return nav.filter((item) => item.requires == null || atLeastRole(user, product, item.requires.role));
}

/**
 * What a dead-end page (not found, module unavailable) offers as the way out: a button
 * into the landing module, picked as "/" would pick it (`lastModule` is the in-memory
 * copy), and the hint to use the module menu only when there IS a menu, which the
 * header shows only for two or more modules.
 */
export function deadEndHelp(available, lastModule) {
  return { landing: pickLanding(available, lastModule), menuHint: available.length > 1 };
}

/** Why someone with no available module has none (Review Focus 2). */
export function noAccessReason(user, manifests) {
  return manifests.some((mf) => holds(user, mf.product)) ? 'not-enabled' : 'none';
}

/** Why one particular module is not open to this user. */
export function moduleUnavailableReason(manifest, user) {
  return holds(user, manifest.product) ? 'not-enabled' : 'not-held';
}
